import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Portal from "../../utils/Portal";
import { registerConfirmDialog } from "../../services/confirmDialog";
import styles from "./ConfirmDialogHost.module.css";

let confirmRequestId = 1;

function ToneGlyph({ tone }) {
  if (tone === "danger") {
    return (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
        <path
          d="M12 8v5m0 3h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.72 3h16.92a2 2 0 0 0 1.72-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
      <path
        d="M12 16v.01M12 8a4 4 0 0 1 4 4c0 2.21-1.79 3-4 4-2.21-1-4-1.79-4-4a4 4 0 0 1 4-4Zm0-5 7 3v5c0 5-3.5 9-7 10-3.5-1-7-5-7-10V6l7-3Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function ConfirmDialogHost() {
  const [queue, setQueue] = useState([]);
  const confirmButtonRef = useRef(null);
  const previousFocusRef = useRef(null);

  const activeRequest = queue[0] || null;

  const activeDialog = useMemo(() => {
    if (!activeRequest) return null;
    return {
      title: activeRequest.title || "Please confirm",
      message: activeRequest.message || "",
      confirmLabel: activeRequest.confirmLabel || "Confirm",
      cancelLabel: activeRequest.cancelLabel || "Cancel",
      tone: activeRequest.tone || "danger",
      testId: activeRequest.testId || undefined,
    };
  }, [activeRequest]);

  const settleRequest = useCallback((accepted) => {
    setQueue((currentQueue) => {
      const [current, ...rest] = currentQueue;
      if (current?.resolve) {
        current.resolve(Boolean(accepted));
      }
      return rest;
    });
  }, []);

  const openConfirm = useCallback(
    (options) =>
      new Promise((resolve) => {
        setQueue((currentQueue) => [
          ...currentQueue,
          {
            id: confirmRequestId++,
            ...options,
            resolve,
          },
        ]);
      }),
    []
  );

  useEffect(() => registerConfirmDialog(openConfirm), [openConfirm]);

  useEffect(() => {
    if (!activeRequest) return undefined;

    previousFocusRef.current = document.activeElement;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        settleRequest(false);
      }
    };

    // Focus and the Escape listener both wait one task. A prompt is often
    // raised from inside a keydown handler (a dialog's own Escape guard, for
    // one). For a real keystroke the browser runs a microtask checkpoint
    // between listeners, so React commits this effect while that keydown is
    // still bubbling towards `document`; a listener added right here would
    // receive the very keystroke that opened the prompt and cancel it before
    // it was ever seen.
    const timer = window.setTimeout(() => {
      confirmButtonRef.current?.focus();
      document.addEventListener("keydown", handleKeyDown);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("keydown", handleKeyDown);
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === "function") {
        previousFocusRef.current.focus();
      }
    };
  }, [activeRequest, settleRequest]);

  if (!activeDialog) return null;

  return (
    <Portal>
      <div
        className={styles.overlay}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            settleRequest(false);
          }
        }}
      >
        <div
          className={`${styles.dialog} ${
            activeDialog.tone === "danger" ? styles.dialogDanger : ""
          }`.trim()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="global-confirm-title"
          aria-describedby="global-confirm-message"
          data-testid={activeDialog.testId}
        >
          <div className={styles.header}>
            <div
              className={`${styles.iconWrap} ${
                activeDialog.tone === "danger" ? styles.iconWrapDanger : ""
              }`.trim()}
            >
              <ToneGlyph tone={activeDialog.tone} />
            </div>

            <div className={styles.copy}>
              <h2 id="global-confirm-title" className={styles.title}>
                {activeDialog.title}
              </h2>
              <p id="global-confirm-message" className={styles.message}>
                {activeDialog.message}
              </p>
            </div>
          </div>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelButton}
              data-testid={activeDialog.testId ? `${activeDialog.testId}-cancel` : undefined}
              onClick={() => settleRequest(false)}
            >
              {activeDialog.cancelLabel}
            </button>
            <button
              ref={confirmButtonRef}
              type="button"
              className={`${styles.confirmButton} ${
                activeDialog.tone === "danger" ? styles.confirmButtonDanger : ""
              }`.trim()}
              data-testid={activeDialog.testId ? `${activeDialog.testId}-accept` : undefined}
              onClick={() => settleRequest(true)}
            >
              {activeDialog.confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}

